import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ChevronDown, ChevronUp, Package, AlertCircle, Loader2 } from 'lucide-react';
import httpClient from '../services/httpClient';

const DeliveryAlertsCard = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedOrders, setExpandedOrders] = useState(new Set());

  useEffect(() => {
    fetchDeliveryAlerts();
  }, []);

  const fetchDeliveryAlerts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await httpClient.get('/api/dashboard/delivery-alerts');
      if (response.data && response.data.success) {
        setAlerts(response.data.data || []);
      } else {
        setAlerts([]);
      }
    } catch (err) {
      console.error('Error fetching delivery alerts:', err);
      setError('加载交付预警失败');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const handleOrderClick = (orderId, type = 'sales') => {
    if (type === 'sales') {
      navigate(`/sales/${orderId}/view`);
    } else if (type === 'purchase') {
      navigate(`/purchases/${orderId}/view`);
    } else if (type === 'production') {
      navigate(`/production/${orderId}`);
    }
  };

  const formatDaysText = (days, isOverdue) => {
    if (isOverdue) {
      return `已逾期${Math.abs(days)}天`;
    }
    return `${days}天后`;
  };

  if (loading) {
    return (
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            交付日期预警
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            交付日期预警
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-destructive">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p>{error}</p>
            <Button variant="outline" size="sm" onClick={fetchDeliveryAlerts} className="mt-4">
              重试
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (alerts.length === 0) {
    return (
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            交付日期预警
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>暂无即将交付的订单</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-8" data-testid="delivery-alerts-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          交付日期预警
          <Badge variant="secondary" className="ml-2">{alerts.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {alerts.map((order) => (
            <div key={order._id} className="p-4 hover:bg-muted/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <button
                      onClick={() => handleOrderClick(order._id, 'sales')}
                      className="text-primary underline hover:text-primary/80 transition-colors font-medium"
                    >
                      {order.sales_order_id}
                    </button>
                    <span className="text-muted-foreground">({order.client_name})</span>
                    {order.is_overdue && (
                      <Badge variant="destructive" className="text-xs">已逾期</Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    交付日期: {new Date(order.delivery_date).toLocaleDateString('zh-CN')} 
                    <span className={order.is_overdue ? 'text-destructive font-medium' : 'text-warning'}>
                      {' '}({formatDaysText(order.days_until_delivery, order.is_overdue)})
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleExpand(order._id)}
                  className="ml-2"
                >
                  {expandedOrders.has(order._id) ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {expandedOrders.has(order._id) && (
                <div className="mt-4 pl-4 border-l-2 border-muted">
                  {order.linked_purchase_orders && order.linked_purchase_orders.length > 0 && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-muted-foreground mb-2">关联采购单:</p>
                      <div className="space-y-1">
                        {order.linked_purchase_orders.map((po) => (
                          <div key={po._id} className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">•</span>
                            <button
                              onClick={() => handleOrderClick(po._id, 'purchase')}
                              className="text-primary underline hover:text-primary/80 transition-colors"
                            >
                              {po.purchase_order_id}
                            </button>
                            <Badge variant="outline" className="text-xs">{po.status}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {order.linked_production_orders && order.linked_production_orders.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">关联工单:</p>
                      <div className="space-y-1">
                        {order.linked_production_orders.map((prod) => (
                          <div key={prod._id} className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">•</span>
                            <button
                              onClick={() => handleOrderClick(prod._id, 'production')}
                              className="text-primary underline hover:text-primary/80 transition-colors"
                            >
                              {prod.production_order_id}
                            </button>
                            <Badge variant="outline" className="text-xs">{prod.status}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(!order.linked_purchase_orders || order.linked_purchase_orders.length === 0) &&
                   (!order.linked_production_orders || order.linked_production_orders.length === 0) && (
                    <p className="text-sm text-muted-foreground">暂无关联订单</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default DeliveryAlertsCard;
